export type ElicitationOutcome = "accepted" | "declined" | "cancelled" | "error";

export type UiDecision =
  | "accept"
  | "acceptForSession"
  | "decline"
  | "cancel"
  | { acceptWithExecpolicyAmendment: { execpolicy_amendment: string[] } }
  | { applyNetworkPolicyAmendment: { network_policy_amendment: { host: string; action: "allow" | "deny" } } };

export type ElicitationWireResponse = {
  outcome: ElicitationOutcome;
  result?: unknown;
  error?: { code: string; message: string; retryable: boolean };
};

export function commandApprovalDecisions(paramsValue: unknown): UiDecision[] | null {
  const params = record(paramsValue);
  if (!Array.isArray(params.availableDecisions)) return null;
  const result: UiDecision[] = [];
  const seen = new Set<string>();
  for (const candidate of params.availableDecisions) {
    let decision: UiDecision | null = null;
    if (["accept", "acceptForSession", "decline", "cancel"].includes(String(candidate))) {
      decision = candidate as UiDecision;
    } else {
      const value = record(candidate);
      const execpolicy = record(value.acceptWithExecpolicyAmendment).execpolicy_amendment;
      if (Array.isArray(execpolicy) && execpolicy.length > 0 && execpolicy.every((item) => typeof item === "string")) {
        decision = { acceptWithExecpolicyAmendment: { execpolicy_amendment: [...execpolicy] } };
      } else {
        const network = record(record(value.applyNetworkPolicyAmendment).network_policy_amendment);
        const host = typeof network.host === "string" ? network.host.trim() : "";
        if (host && (network.action === "allow" || network.action === "deny")) {
          decision = {
            applyNetworkPolicyAmendment: {
              network_policy_amendment: { host, action: network.action },
            },
          };
        }
      }
    }
    if (!decision) continue;
    const fingerprint = JSON.stringify(decision);
    if (seen.has(fingerprint)) continue;
    seen.add(fingerprint);
    result.push(decision);
  }
  return result;
}

function record(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" && !Array.isArray(value) ? value as Record<string, unknown> : {};
}

function outcomeFor(decision: UiDecision): Exclude<ElicitationOutcome, "error"> {
  if (decision === "decline") return "declined";
  if (decision === "cancel") return "cancelled";
  return "accepted";
}

function answerArray(value: string | string[] | undefined): string[] {
  if (Array.isArray(value)) return value.filter(Boolean);
  return value ? [value] : [];
}

function coerceMcpContent(params: Record<string, unknown>, answers: Record<string, string | string[]>): Record<string, unknown> {
  const schema = record(params.requestedSchema);
  const properties = record(schema.properties);
  const content: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(answers)) {
    const definition = record(properties[key]);
    const type = String(definition.type || "string");
    const scalar = Array.isArray(value) ? value[0] || "" : value;
    if (type === "boolean") content[key] = scalar === "true";
    else if (type === "number" || type === "integer") {
      if (!String(scalar).trim()) continue;
      const number = Number(scalar);
      if (!Number.isFinite(number) || (type === "integer" && !Number.isInteger(number))) {
        throw new Error(`${key} 必须是有效的${type === "integer" ? "整数" : "数值"}`);
      }
      content[key] = number;
    } else if (type === "array") content[key] = answerArray(value);
    else content[key] = scalar;
  }
  return content;
}

/**
 * Convert a single UI decision into the exact response object expected by the
 * corresponding Codex app-server reverse request. The gateway forwards only
 * `result` to app-server; `outcome` remains transport metadata.
 */
export function buildElicitationResponse(
  method: string,
  paramsValue: unknown,
  decision: UiDecision,
  answers: Record<string, string | string[]> = {},
): ElicitationWireResponse {
  const params = record(paramsValue);
  const outcome = outcomeFor(decision);

  if (method === "account/chatgptAuthTokens/refresh" || method === "attestation/generate") {
    return {
      outcome: "error",
      error: {
        code: "SERVER_SIDE_PROVIDER_REQUIRED",
        message: "该反向请求必须由服务器认证或证明提供方处理，不能转发到普通客户端",
        retryable: false,
      },
    };
  }

  if (method === "item/commandExecution/requestApproval") {
    return { outcome, result: { decision } };
  }

  if (method === "item/fileChange/requestApproval") {
    const normalized = typeof decision === "string" ? decision : "accept";
    return { outcome, result: { decision: normalized } };
  }

  if (method === "item/permissions/requestApproval") {
    const permissions = outcome === "accepted" ? record(params.permissions) : {};
    return {
      outcome,
      result: {
        permissions,
        scope: decision === "acceptForSession" ? "session" : "turn",
      },
    };
  }

  if (method === "mcpServer/elicitation/request") {
    const action = outcome === "accepted" ? "accept" : outcome === "declined" ? "decline" : "cancel";
    return {
      outcome,
      result: {
        action,
        content: action === "accept" ? coerceMcpContent(params, answers) : null,
        _meta: null,
      },
    };
  }

  if (method === "item/tool/requestUserInput") {
    const questions = Array.isArray(params.questions) ? params.questions : [];
    const responseAnswers: Record<string, { answers: string[] }> = {};
    for (const question of questions) {
      const id = String(record(question).id || "");
      if (id) responseAnswers[id] = { answers: outcome === "accepted" ? answerArray(answers[id]) : [] };
    }
    return { outcome, result: { answers: responseAnswers } };
  }

  if (method === "item/tool/call") {
    return {
      outcome: "error",
      error: {
        code: "SERVER_SIDE_DYNAMIC_TOOL_REQUIRED",
        message: "动态工具必须由可信服务器执行，当前客户端不会伪造成功结果",
        retryable: false,
      },
    };
  }

  if (method === "applyPatchApproval" || method === "execCommandApproval") {
    const legacyDecision = decision === "accept"
      ? "approved"
      : decision === "acceptForSession"
        ? "approved_for_session"
        : decision === "cancel"
          ? "abort"
          : "denied";
    return { outcome, result: { decision: legacyDecision } };
  }

  return {
    outcome: "error",
    error: {
      code: "UNSUPPORTED_SERVER_REQUEST",
      message: `客户端不支持服务器反向请求：${method || "unknown"}`,
      retryable: false,
    },
  };
}
